// PremiumContext — surfaces whether the current authenticated user has
// a premium subscription. Source of truth is the backend; this context
// just fetches + caches in memory and refreshes on window focus.
//
// Wire shape: GET /api/v1/payments/subscription-status returns
//   { tier, premium, stripe_customer_id, status, current_period_end }
// where `premium: bool` is the field this context reads. If the
// backend moves to /api/v1/me (co-founder's call), swap STATUS_URL.
//
// Fail-CLOSED: signed-out, loading, network error, missing field →
// isPremium=false, so the free-tier UI is the default. The only way
// to be marked premium is for the backend to explicitly return
// `premium: true`. New sign-ups land on free; existing users are
// expected to be grandfathered to premium=true server-side.
//
// Logged-in user with backend-grandfathered premium → premium UI.
// Logged-out user → free UI. New sign-up (not yet approved) → free UI.
//
// Org-level premium: if the org has branding.premium = true, all users
// (including logged-out) on that subdomain are treated as premium.

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrg } from './OrgContext';

const STATUS_URL = '/api/v1/payments/subscription-status';
const REFRESH_MIN_INTERVAL_MS = 30_000;

const PremiumContext = createContext({
  isPremium: false,   // FAIL-CLOSED — see file header.
  loading: true,
  refresh: () => {},
});

export function PremiumProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const { branding, loading: orgLoading } = useOrg();
  const [isPremium, setIsPremium] = useState(false);   // FAIL-CLOSED — see file header.
  // Why the user is premium: 'org' (tenant subdomain flag), 'user' (own
  // subscription) or 'none'. Shown under the email in the header so a
  // "why am I premium?" question is a glance, not an investigation.
  const [premiumSource, setPremiumSource] = useState('none');
  const [loading, setLoading] = useState(true);
  const lastFetchedAtRef = useRef(0);

  // Check if org-level premium is set (no auth required)
  const orgPremium = branding?.premium === true;

  const fetchStatus = useCallback(async () => {
    // Org-level premium overrides user-level check
    if (orgPremium) {
      setIsPremium(true);
      setPremiumSource('org');
      setLoading(false);
      return;
    }
    
    if (!isAuthenticated) {
      // Signed-out users see free-tier UI by default. They hit 401 on
      // gated endpoints anyway; the visual gate matches.
      setIsPremium(false);
      setLoading(false);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const apiBase = import.meta.env?.VITE_API_URL || '';
      const resp = await fetch(`${apiBase}${STATUS_URL}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        // FAIL-CLOSED: 401/500/etc → assume non-premium.
        setIsPremium(false);
        return;
      }
      const data = await resp.json();
      // Must EXPLICITLY return premium: true to unlock. Missing field
      // or any other value → free tier.
      setIsPremium(data?.premium === true);
      setPremiumSource(data?.premium === true ? (data?.premium_source || 'user') : 'none');
    } catch {
      // FAIL-CLOSED: network error → assume non-premium. Backend
      // remains the security boundary; this is UX-layer only.
      setIsPremium(false);
    } finally {
      lastFetchedAtRef.current = Date.now();
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, orgPremium]);

  // Initial fetch once Auth0 and Org context finish hydrating.
  useEffect(() => {
    if (authLoading || orgLoading) return;
    fetchStatus();
  }, [authLoading, orgLoading, fetchStatus]);

  // Re-fetch when the tab regains focus, with a 30s throttle so rapid
  // alt-tabbing doesn't hammer the endpoint. Covers the common case of
  // "user paid in a Stripe tab, comes back here expecting unlocked UI."
  useEffect(() => {
    function onFocus() {
      if (Date.now() - lastFetchedAtRef.current < REFRESH_MIN_INTERVAL_MS) return;
      fetchStatus();
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchStatus]);

  return (
    <PremiumContext.Provider value={{ isPremium, premiumSource, loading, refresh: fetchStatus }}>
      {children}
    </PremiumContext.Provider>
  );
}

export function useIsPremium() {
  return useContext(PremiumContext);
}
